const GENRE_MAP: Record<string, string> = {
  m: "Mâle", male: "Mâle", mâle: "Mâle", masculin: "Mâle",
  garçon: "Mâle", garcon: "Mâle", boy: "Mâle",
  f: "Femelle", female: "Femelle", femelle: "Femelle", féminin: "Femelle",
  feminin: "Femelle", fille: "Femelle", girl: "Femelle",
};

function removeAccents(str: string): string {
  return str.normalize("NFD").replace(/[̀-ͯ]/g, "");
}

function normalizeStr(str: string): string {
  return removeAccents(str).toLowerCase().replace(/[\s\-_'.]+/g, "").trim();
}

export function normalizeGenre(raw: string): { value: string; matched: boolean; suggestion?: string } {
  if (!raw || !raw.trim()) return { value: "", matched: false };
  const key = normalizeStr(raw);
  if (GENRE_MAP[key]) {
    return { value: GENRE_MAP[key], matched: true };
  }
  const keys = Object.keys(GENRE_MAP);
  for (const k of keys) {
    if (key.includes(k) || k.includes(key)) {
      return { value: GENRE_MAP[k], matched: true };
    }
  }
  return { value: raw.trim(), matched: false, suggestion: "Mâle ou Femelle" };
}

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

export interface ClasseInfo {
  _id: string;
  nom_classe: string;
}

export function matchClasse(
  raw: string,
  classes: ClasseInfo[]
): { id: string | null; name: string; matched: boolean; suggestion?: string } {
  if (!raw || !raw.trim()) return { id: null, name: "", matched: false };
  const input = raw.trim();
  const inputNorm = normalizeStr(input);

  // Level 1: exact match (case-insensitive, trimmed)
  for (const c of classes) {
    if (c.nom_classe.trim().toLowerCase() === input.toLowerCase()) {
      return { id: c._id, name: c.nom_classe, matched: true };
    }
  }

  // Level 2: normalized match (remove accents, spaces, dashes)
  for (const c of classes) {
    if (normalizeStr(c.nom_classe) === inputNorm) {
      return { id: c._id, name: c.nom_classe, matched: true };
    }
  }

  // Level 2.5: input contains class name or vice versa
  for (const c of classes) {
    const cn = normalizeStr(c.nom_classe);
    if (inputNorm.includes(cn) || cn.includes(inputNorm)) {
      return { id: c._id, name: c.nom_classe, matched: true };
    }
  }

  // Level 3: Levenshtein distance — find closest
  let best: ClasseInfo | null = null;
  let bestDist = Infinity;
  for (const c of classes) {
    const d = levenshtein(inputNorm, normalizeStr(c.nom_classe));
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  const maxLen = Math.max(inputNorm.length, best ? normalizeStr(best.nom_classe).length : 1);
  if (best && bestDist / maxLen <= 0.4) {
    return { id: best._id, name: best.nom_classe, matched: true };
  }

  const suggestion = best ? best.nom_classe : undefined;
  return { id: null, name: input, matched: false, suggestion };
}

export interface FieldError {
  field: string;
  message: string;
  suggestion?: string;
}

export interface ValidatedRow {
  index: number;
  original: Record<string, any>;
  normalized: Record<string, any>;
  errors: FieldError[];
  corrected: Record<string, string>;
  valid: boolean;
}

export function validateRow(
  row: Record<string, any>,
  index: number,
  classes: ClasseInfo[]
): ValidatedRow {
  const errors: FieldError[] = [];
  const corrected: Record<string, string> = {};
  const normalized: Record<string, any> = { ...row };

  // nom_eleve
  const nomEleve = (row.nom_eleve || "").toString().trim();
  if (!nomEleve) {
    errors.push({ field: "nom_eleve", message: "Nom élève requis" });
  } else if (nomEleve.length < 2) {
    errors.push({ field: "nom_eleve", message: "Min 2 caractères" });
  }
  normalized.nom_eleve = nomEleve;

  // prenom_eleve
  const prenomEleve = (row.prenom_eleve || "").toString().trim();
  if (!prenomEleve) {
    errors.push({ field: "prenom_eleve", message: "Prénom élève requis" });
  } else if (prenomEleve.length < 2) {
    errors.push({ field: "prenom_eleve", message: "Min 2 caractères" });
  }
  normalized.prenom_eleve = prenomEleve;

  // genre
  const genreRaw = (row.genre || "").toString().trim();
  if (!genreRaw) {
    errors.push({ field: "genre", message: "Genre requis" });
  } else {
    const g = normalizeGenre(genreRaw);
    if (!g.matched) {
      errors.push({ field: "genre", message: `Genre non reconnu`, suggestion: g.suggestion });
    } else {
      normalized.genre = g.value;
      if (g.value !== genreRaw) {
        corrected.genre = g.value;
      }
    }
  }

  // classe
  const classeRaw = (row.classe || "").toString().trim();
  if (!classeRaw) {
    errors.push({ field: "classe", message: "Classe requise" });
  } else {
    const cm = matchClasse(classeRaw, classes);
    if (!cm.matched) {
      errors.push({
        field: "classe",
        message: `Classe "${classeRaw}" introuvable`,
        suggestion: cm.suggestion ? `Vouliez-vous dire "${cm.suggestion}" ?` : undefined,
      });
    } else {
      normalized.classe = cm.name;
      normalized._classeId = cm.id;
      if (cm.name !== classeRaw) {
        corrected.classe = cm.name;
      }
    }
  }

  // nom_parent
  const nomParent = (row.nom_parent || "").toString().trim();
  if (!nomParent) {
    errors.push({ field: "nom_parent", message: "Nom parent requis" });
  }
  normalized.nom_parent = nomParent;

  // prenom_parent
  const prenomParent = (row.prenom_parent || "").toString().trim();
  if (!prenomParent) {
    errors.push({ field: "prenom_parent", message: "Prénom parent requis" });
  }
  normalized.prenom_parent = prenomParent;

  // phone_parent
  const phone = (row.phone_parent || "").toString().trim();
  if (!phone) {
    errors.push({ field: "phone_parent", message: "Téléphone parent requis" });
  } else if (!/^[0-9]{8}$/.test(phone)) {
    errors.push({ field: "phone_parent", message: "8 chiffres requis" });
  }
  normalized.phone_parent = phone;

  return { index, original: row, normalized, errors, corrected, valid: errors.length === 0 };
}

export function validateAllRows(rows: Record<string, any>[], classes: ClasseInfo[]): ValidatedRow[] {
  return rows.map((row, i) => validateRow(row, i, classes));
}
